import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, RSD, fmtDateTime } from "../../lib/api";
import { useOrderSocket } from "../../hooks/useOrderSocket";
import { useAuth } from "../../context/AuthContext";
import { NotifyButton } from "../../components/NotifyButton";
import { useCart } from "../../context/CartContext";
import { useI18n } from "../../context/I18nContext";
import { L } from "../../lib/localize";
import { BagLogo, BagLogoTile } from "../../components/Logo";
import { OrderTimeline } from "../../components/OrderTimeline";
import { OrderCountdown } from "../../components/OrderCountdown";
import { useInstallSuppress } from "../../lib/pwaInstall";
import { ArrowLeft, Check, Clock, ChefHat, PackageCheck, X, Bell, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "new", label: "Poslato", icon: Check },
  { key: "accepted", label: "Prihvaćeno", icon: Check },
  { key: "preparing", label: "Priprema se", icon: ChefHat },
  { key: "ready", label: "Spremno", icon: PackageCheck },
  { key: "completed", label: "Preuzeto", icon: Check },
];
const ORDER_RANK = { new: 0, time_change_pending: 0, accepted: 1, preparing: 2, ready: 3, completed: 4 };

function notify(title, body) {
  if ("Notification" in window && Notification.permission === "granted") {
    try { new Notification(title, { body, icon: "/icon-192.png?v=8" }); } catch {}
  }
}

export default function OrderTracking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, clear, restaurant: cartRest, items: cartItems, setPrefs } = useCart();
  const { user } = useAuth();
  const { t, localePath, locale } = useI18n();
  const [order, setOrder] = useState(null);
  const [events, setEvents] = useState([]);
  const [remaining, setRemaining] = useState(0);
  const [notifOn, setNotifOn] = useState(("Notification" in window) && Notification.permission === "granted");
  const [confirmPickup, setConfirmPickup] = useState(false);
  const prevStatus = useRef(null);

  const markPickedUp = async () => {
    try {
      const { data } = await api.post(`/orders/${id}/complete`);
      apply(data);
      setConfirmPickup(false);
      toast.success(t("toast.completed", "Prijatno! Porudžbina preuzeta."));
    } catch (e) {
      setConfirmPickup(false);
      toast.error(e?.response?.data?.detail?.message || t("common.error", "Greška"));
    }
  };

  const load = async () => {
    try { const { data } = await api.get(`/orders/${id}`); apply(data); } catch { }
    try { const { data } = await api.get(`/orders/${id}/events`); setEvents(data || []); } catch { }
  };

  const lastUpdatedAt = useRef(null);

  const apply = (data) => {
    if (!data) return;
    // Monotonic guard: never let an older fetch overwrite a newer realtime update (race safety).
    if (data.updated_at && lastUpdatedAt.current && data.updated_at < lastUpdatedAt.current) return;
    if (data.updated_at) lastUpdatedAt.current = data.updated_at;
    if (prevStatus.current && prevStatus.current !== data.status) {
      const map = {
        accepted: t("toast.accepted", "Porudžbina prihvaćena!"),
        preparing: t("toast.preparing", "Vaša porudžbina se priprema"),
        ready: t("toast.ready", "Vaša porudžbina je spremna!"),
        completed: t("toast.completed", "Prijatno! Porudžbina preuzeta."),
        cancelled: t("toast.cancelled", "Porudžbina je otkazana"),
        rejected: t("toast.rejected", "Porudžbina je odbijena"),
        time_change_pending: t("toast.time_change", "Predloženo novo vreme preuzimanja"),
      };
      if (map[data.status]) { toast(map[data.status]); notify("Ponesi", map[data.status]); }
    }
    prevStatus.current = data.status;
    setOrder(data);
  };

  useEffect(() => { load(); }, [id]);

  // Realtime UPDATE subscription for THIS order (scoped to the owner via user_id) +
  // reconnect/visibility/online recovery via onReady.
  useOrderSocket((msg) => {
    if ((msg.type === "order_update" || msg.type === "new_order") && msg.order?.id === id) apply(msg.order);
  }, { userId: user?.user_id, onReady: load });

  // Active-order fallback polling (safety net). Stops at a terminal status.
  useEffect(() => {
    if (!order || ["completed", "cancelled", "rejected"].includes(order.status)) return;
    const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, 12000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [order?.status]);

  const expiredFetched = useRef(false);
  // 60s countdown for time change (server-controlled deadline). Fetch ONCE at expiry.
  useEffect(() => {
    if (order?.status !== "time_change_pending" || !order.time_change_deadline) return;
    expiredFetched.current = false;
    let t;
    const tick = () => {
      const diff = Math.max(0, Math.floor((new Date(order.time_change_deadline) - new Date()) / 1000));
      setRemaining(diff);
      if (diff === 0 && !expiredFetched.current) { expiredFetched.current = true; clearInterval(t); load(); }
    };
    tick();
    t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [order?.status, order?.time_change_deadline]);

  // Never interrupt while waiting for confirmation or during the 60s pickup-time decision.
  useInstallSuppress(order?.status === "new" || order?.status === "time_change_pending");

  const respond = async (accept) => {
    try { const { data } = await api.post(`/orders/${id}/respond-time`, { accept }); apply(data); }
    catch (e) { const d = e?.response?.data?.detail; toast.error((d && d.message) || d || "Greška"); load(); }
  };

  const cancelOrder = async () => {
    if (!window.confirm(t("order.confirm_cancel", "Otkazati porudžbinu?"))) return;
    try { const { data } = await api.post(`/orders/${id}/cancel`); apply(data); toast.success(t("order.cancelled_toast", "Porudžbina otkazana")); }
    catch (e) { toast.error(e?.response?.data?.detail || "Greška"); load(); }
  };

  const reorder = async () => {
    // Cross-restaurant single-cart guard: confirm before discarding a cart from another restaurant.
    if (cartItems.length > 0 && cartRest && cartRest.slug !== order.restaurant_slug) {
      if (!window.confirm(t("cart.switch_restaurant", "Imate proizvode iz drugog restorana. Želite li da ispraznite korpu i započnete novu?"))) return;
    }
    try {
      const { data } = await api.get(`/restaurants/${order.restaurant_slug}/menu`);
      const byId = Object.fromEntries(data.products.map((p) => [p.id, p]));
      clear();
      let skipped = 0;
      const rest = { slug: order.restaurant_slug, name: order.restaurant_name, id: order.restaurant_id };
      for (const it of order.items) {
        const p = byId[it.product_id];
        if (!p || !p.available) { skipped++; continue; }
        const all = [...(p.modifier_groups || []).flatMap((g) => g.options || []), ...(p.extras || [])];
        const optionIds = (it.modifiers || []).map((m) => all.find((o) => o.name === m.name)?.id).filter(Boolean);
        addItem(p, optionIds, it.quantity, it.note || "", rest, it.person_label || "");
      }
      if (skipped > 0) toast(`${skipped} ${t("order.items_skipped_suffix", "stavki nije dostupno i preskočeno je")}`);
      // Order again: carry over the ORIGINAL canonical choices as convenience defaults.
      // ASAP is reusable (earliest time is recalculated server-side); a historical scheduled
      // timestamp is not, so only the "scheduled" mode is restored. Payment validity is
      // re-checked in checkout against current restaurant settings.
      setPrefs({
        pickup_type: order.pickup_type === "scheduled" ? "scheduled" : "asap",
        payment_method: order.payment_method === "card" ? "card" : "cash",
      });
      toast.success(t("order.reorder_added", "Dodato u korpu po aktuelnim cenama"));
      navigate(localePath(`/restaurant/${order.restaurant_slug}`));
    } catch { toast.error(t("common.error", "Greška pri ponovnom naručivanju")); }
  };

  if (!order) {
    return <div className="min-h-screen flex items-center justify-center bg-brand-red"><div className="animate-pop"><BagLogoTile size={88} /></div></div>;
  }

  const cancelled = ["cancelled", "rejected"].includes(order.status);
  const rank = ORDER_RANK[order.status] ?? 0;

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <div className="max-w-md mx-auto bg-[#F9F9F7] min-h-screen pb-10">
        <header className="bg-brand-red text-white rounded-b-3xl px-5 pt-5 pb-8 relative">
          <button data-testid="back-home" onClick={() => navigate(localePath("/"))} className="absolute top-5 left-5 bg-white/15 rounded-full p-2"><ArrowLeft size={18} /></button>
          <div className="text-center mt-2">
            <p className="text-white/80 text-sm">{t("order.title", "Porudžbina")}</p>
            <h1 data-testid="order-number" className="font-display font-black text-5xl tracking-tight">{order.public_code || order.order_number}</h1>
            <p className="text-white/70 text-xs mt-1">{t("order.tell_number", "Recite ovaj broj osoblju prilikom preuzimanja.")}</p>
          </div>
        </header>

        <div className="px-5 mt-5 space-y-4">
          {!cancelled && order.status !== "completed" && order.status !== "time_change_pending" && (
            <OrderCountdown order={order} />
          )}
          {cancelled ? (
            <div className="bg-white rounded-3xl p-6 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="order-cancelled">
              <div className="w-14 h-14 rounded-full bg-brand-red/10 text-brand-red flex items-center justify-center mx-auto mb-3"><X size={28} /></div>
              <h2 className="font-display font-black text-xl">{order.status === "rejected" ? t("order.rejected", "Porudžbina odbijena") : t("order.cancelled", "Porudžbina otkazana")}</h2>
              <p className="text-sm text-slate-500 mt-1" data-testid="cancel-reason">
                {order.cancel_reason === "time_change_timeout" && t("order.reason_timeout", "Isteklo je vreme za potvrdu nove vremenske izmene (60s).")}
                {order.cancel_reason === "customer" && t("order.reason_customer", "Otkazali ste porudžbinu.")}
                {order.cancel_reason === "restaurant" && t("order.reason_restaurant", "Restoran nije mogao da prihvati porudžbinu.")}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="status-card">
              <div className="flex justify-between">
                {STEPS.map((s, i) => {
                  const done = i <= rank;
                  const Icon = s.icon;
                  return (
                    <div key={s.key} className="flex flex-col items-center flex-1">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${done ? "bg-brand-green text-white" : "bg-slate-100 text-slate-400"}`}>
                        <Icon size={16} />
                      </div>
                      <span className={`text-[10px] mt-1 text-center font-semibold ${done ? "text-slate-800" : "text-slate-400"}`}>{t("status." + s.key, s.label)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Persistent waiting card — visible only while awaiting restaurant response (status NEW).
              Above the fold, above details. Auto-hides on accept/reject/cancel/time-change via realtime. */}
          {order.status === "new" && (
            <div data-testid="waiting-card" role="status" aria-live="polite" className="bg-white rounded-3xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border-2 border-brand-orange/30">
              <div className="flex items-center justify-center gap-2 text-brand-orange">
                <span className="w-4 h-4 rounded-full border-2 border-brand-orange/30 border-t-brand-orange animate-spin" />
                <p className="text-xs font-bold uppercase tracking-widest">{t("order.waiting_title", "Čeka se potvrda restorana")}</p>
              </div>
              <p className="text-center font-display font-black text-lg mt-3 leading-snug">{t("order.waiting_body", "Molimo vas ne napuštajte stranicu dok restoran ne prihvati vašu porudžbinu. Hvala.")}</p>
              <p className="text-center text-sm text-slate-500 mt-2">{t("order.waiting_secondary", "Odgovor restorana će se pojaviti automatski.")}</p>
            </div>
          )}

          {/* Time change 60s */}
          {order.status === "time_change_pending" && (
            <div className="bg-white rounded-3xl p-5 shadow-lg border-2 border-brand-orange animate-pop" data-testid="time-change-card">
              <h3 className="font-display font-black text-lg text-center">{t("order.newtime_title", "Novo vreme preuzimanja")}</h3>
              <p className="text-center text-xs text-slate-400 mt-1">{t("order.original_time", "Prvobitno vreme")}: <b className="text-slate-600">{order.requested_time || order.pickup_time}</b></p>
              <p className="text-center font-display font-black text-3xl text-brand-orange my-2">{order.proposed_time}</p>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-brand-red transition-all duration-500" style={{ width: `${(remaining / 60) * 100}%` }} />
              </div>
              <p className="text-center text-xs text-slate-400 mt-1">{remaining}{t("order.seconds_left", "s za odgovor")}</p>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button data-testid="accept-time" onClick={() => respond(true)} className="bg-white text-brand-red border-2 border-brand-red rounded-full py-3 font-bold active:scale-[0.98] transition-transform">{t("order.accept", "Prihvati")} {order.proposed_time}</button>
                <button data-testid="cancel-time" onClick={() => respond(false)} className="bg-white text-slate-600 border-2 border-slate-200 rounded-full py-3 font-bold active:scale-[0.98] transition-transform">{t("common.cancel", "Otkaži")}</button>
              </div>
            </div>
          )}

          {/* Notifications (platform-aware; iOS Safari gets install guidance) */}
          {!cancelled && <NotifyButton variant="pill" />}

          {/* Details */}
          <div className="bg-white rounded-3xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1"><Clock size={15} /> {t("order.order_date", "Datum porudžbine:")} <b className="text-slate-800">{fmtDateTime(order.created_at, locale)}</b></div>
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-3"><Clock size={15} /> {t("order.pickup_time_label", "Vreme preuzimanja:")} <b className="text-slate-800">{order.pickup_time}</b></div>
            <div className="space-y-2">
              {order.items.map((it, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <div className="min-w-0">
                    <span className="font-semibold">{it.quantity}× {L(it, locale)}</span>
                    {it.modifiers?.length > 0 && <span className="text-slate-400"> · {it.modifiers.map((m) => (locale === "en" ? (m.name_en || m.name) : m.name)).join(", ")}</span>}
                    {it.person_label && <div className="text-xs mt-0.5"><span className="font-bold text-brand-red">{t("item.for_prefix", "Za")}: </span><span className="font-semibold text-slate-700">{it.person_label}</span></div>}
                    {it.note && <div className="text-xs text-slate-500 mt-0.5"><span className="font-semibold">{t("item.note_prefix", "Napomena")}: </span><span className="italic">{it.note}</span></div>}
                  </div>
                  <span className="font-semibold shrink-0">{RSD(it.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="border-t mt-3 pt-3 flex justify-between font-display font-black">
              <span>{t("order.total", "Ukupno")} ({order.payment_method === "cash" ? t("order.pay_cash", "keš") : t("order.pay_card", "kartica")})</span>
              <span className="text-brand-red">{RSD(order.total)}</span>
            </div>
          </div>

          {/* Activity timeline (customer-safe audit trail) */}
          <div className="bg-white rounded-3xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="activity-card">
            <h3 className="font-display font-black text-base mb-3">{t("tl.title", "Aktivnost")}</h3>
            <OrderTimeline events={events} staff={false} />
          </div>

          {order.status === "new" && (
            <button data-testid="customer-cancel" onClick={cancelOrder} className="w-full bg-white text-brand-red border-2 border-brand-red/20 rounded-2xl py-3.5 font-bold">
              {t("order.cancel_order", "Otkaži porudžbinu")}
            </button>
          )}
          {order.status === "ready" && (
            <button data-testid="customer-pickup-btn" onClick={() => setConfirmPickup(true)}
              className="w-full bg-brand-green text-white rounded-2xl py-4 font-display font-black text-lg flex items-center justify-center gap-2 shadow-lg active:scale-[0.98] transition-transform">
              <Check size={20} /> {t("order.picked_up", "Preuzeto")}
            </button>
          )}
          {order.status === "completed" && (
            <div data-testid="completed-badge" className="w-full bg-brand-green/10 text-brand-green rounded-2xl py-4 font-display font-black text-lg flex items-center justify-center gap-2">
              <Check size={20} /> {t("order.picked_up", "Preuzeto")}
            </div>
          )}
          {["completed", "cancelled", "rejected"].includes(order.status) && (
            <button data-testid="reorder-btn" onClick={reorder} className="w-full bg-brand-red text-white rounded-2xl py-3.5 font-display font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
              <RotateCcw size={17} /> {t("order.reorder", "Poruči ponovo")}
            </button>
          )}
        </div>
      </div>

      {confirmPickup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="pickup-confirm">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmPickup(false)} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-xs text-center">
            <h3 className="font-display font-black text-lg">{t("order.pickup_confirm_title", "Da li ste preuzeli porudžbinu?")}</h3>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button data-testid="pickup-confirm-no" onClick={() => setConfirmPickup(false)} className="rounded-full py-3 bg-slate-100 font-bold">{t("order.confirm_no", "Ne")}</button>
              <button data-testid="pickup-confirm-yes" onClick={markPickedUp} className="rounded-full py-3 bg-brand-green text-white font-bold">{t("order.confirm_yes", "Da, preuzeto")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
